import { useCallback, useMemo, useState } from "react";
import { isArray, isNull } from "lodash";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next"; // Import useTranslation

import { ROUTES } from "constants/routes";
import { useListOfPolicy } from "hooks/password-policy";
import { validateEmail } from "utils/validators";
import {
  initialTasterData,
  genderSelectOptions,
  educationSelectOptions,
  smokingSelectOptions,
  milkAlternativesOptions,
  milkFrequencyOptions,
  mayonnaiseFrequencyOptions,
  mayonnaiseTypesOptions,
  flavorPreferencesOptions,
  dislikedSubstancesOptions,
  dietTypeOptions,
  contrastingFlavorsEnjoyingOptions,
  subtleFlavorsDetectingOptions,
  cookingOptions,
  dinnerEatingOptions,
  favoriteFoodTextureOptions,
  snacksOptions,
  saltAddingOptions,
  drinksConsumingOptions,
  regularDrinkOptions,
  coffeeInCafeOptions,
  coffeeAtHomeOptions,
  sugarInCoffeeOptions,
  milkInCoffeeOptions,
  regularTeaOptions,
  sugarInTeaOptions,
  spicyEatingOptions,
  tasteSenseOptions,
  smellSenseOptions
} from "../constants";
import { StepContentData } from "interfaces/sign-up";
import { InitialStep } from "../steps/InitialStep";
import { Step } from "../steps/Step";

const useSignUp = () => {
  const { t } = useTranslation(); // Initialize translation
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmedPassword, setConfirmedPassword] = useState<string>("");
  const [tasterData, setTasterData] =
    useState<typeof initialTasterData>(initialTasterData);
  const [loading, setLoading] = useState<boolean>(false);
  const [validFieldErrors, setValidFieldErrors] = useState<string[]>([]);
  const [emailErrorText, setEmailErrorText] = useState<string>("");
  const [passwordErrorText, setPasswordErrorText] = useState<string>("");
  const [confirmedPasswordErrorText, setConfirmedPasswordErrorText] =
    useState<string>("");
  const [agreementErrorText, setAgreementErrorText] = useState<string>("");
  const [agreement, setAgreement] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);

  const navigate = useNavigate();
  const stepsCount = 6;

  const {
    listOfPolicy,
    isMinimum,
    isNumeric,
    isSpecial,
    isLowerCase,
    isUpperCase,
    isPasswordsMatch,
  } = useListOfPolicy({ password, confirmedPassword });

  const handleRedirectToLogIn = useCallback(() => {
    navigate(ROUTES.signIn);
  }, [navigate]);

  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
    setValidFieldErrors((prev) =>
      prev.filter((error: string) => error !== "email")
    );
    setEmailErrorText("");
  }, []);

  const handlePasswordChange = useCallback((value: string) => {
    setPassword(value);
    setValidFieldErrors((prev) =>
      prev.filter((error: string) => error !== "password")
    );
    setPasswordErrorText("");
  }, []);

  const handleConfirmedPasswordChange = useCallback((value: string) => {
    setConfirmedPassword(value);
    setValidFieldErrors((prev) =>
      prev.filter((error: string) => error !== "confirmedPassword")
    );
    setConfirmedPasswordErrorText("");
  }, []);

  const handleAgreementChange = useCallback((value: boolean) => {
    setAgreement(value);
    setAgreementErrorText("");
  }, []);

  const handleFieldChange = useCallback(
    (name: keyof typeof initialTasterData) => (value: string | string[]) => {
      setTasterData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setValidFieldErrors((prev) =>
        prev.filter((error: string) => error !== name)
      );
    },
    []
  );

  const setSelectOptions = useCallback(
    (array: string[]) =>
      array.map((option: string) => ({
        label: option,
        value: option.toLocaleLowerCase().replaceAll(" ", "-"),
      })),
    []
  );

  const checkFieldValidation = useCallback(
    (value: string | string[] | null, key: string) => () => {
      if (!isArray(value)) {
        if (key === "email") {
          if (!value) {
            setValidFieldErrors((prevErrors) =>
              prevErrors.includes(key) ? prevErrors : [...prevErrors, key]
            );
          } else if (!validateEmail(value)) {
            setValidFieldErrors((prevErrors) =>
              prevErrors.includes(key) ? prevErrors : [...prevErrors, key]
            );

            setEmailErrorText(t("email_validation_error"));
          }
        }

        if (key === "password") {
          if (
            !value ||
            !isMinimum ||
            !isLowerCase ||
            !isUpperCase ||
            !isNumeric ||
            !isSpecial
          ) {
            setValidFieldErrors((prevErrors) =>
              prevErrors.includes(key) ? prevErrors : [...prevErrors, key]
            );
          }

          if (!value) {
            setPasswordErrorText(t("password_enter_error"));
          } else if (!isMinimum) {
            setPasswordErrorText(t("password_minimum_error"));
          } else if (!isLowerCase) {
            setPasswordErrorText(t("password_lowercase_error"));
          } else if (!isNumeric) {
            setPasswordErrorText(t("password_numeric_error"));
          } else if (!isSpecial) {
            setPasswordErrorText(t("password_special_error"));
          } else if (!isUpperCase) {
            setPasswordErrorText(t("password_uppercase_error"));
          }
        }

        if (key === "confirmedPassword") {
          if (!value || !isPasswordsMatch) {
            setValidFieldErrors((prevErrors) =>
              prevErrors.includes(key) ? prevErrors : [...prevErrors, key]
            );
          }

          if (
            !!(password && confirmedPassword && password !== confirmedPassword)
          ) {
            setConfirmedPassword(t("password_mismatch_error"));
          }
        }

        if (!value) {
          setValidFieldErrors((prevErrors) =>
            prevErrors.includes(key) ? prevErrors : [...prevErrors, key]
          );
        }

        if (isNull(value)) {
          setValidFieldErrors((prevErrors) =>
            prevErrors.includes(key) ? prevErrors : [...prevErrors, key]
          );
        }
      } else {
        if (value.length === 0) {
          setValidFieldErrors((prevErrors) =>
            prevErrors.includes(key) ? prevErrors : [...prevErrors, key]
          );
        }
      }
    },
    [
      confirmedPassword,
      isLowerCase,
      isMinimum,
      isNumeric,
      isPasswordsMatch,
      isSpecial,
      isUpperCase,
      password,
      t, // Add translation function to dependencies
    ]
  );

  const checkFieldsValidation = useCallback(
    (values: { [key: string]: string | string[] | null }) => {
      Object.entries(values).forEach(([key, value]) => {
        checkFieldValidation(value, key)();
      });

      if (step === 1 && !agreement) {
        setAgreementErrorText(t("agreement_error"));
      }
    },
    [agreement, checkFieldValidation, step, t] // Add translation function to dependencies
  );

  const isPasswordValid = useMemo(
    () =>
      !!(
        isLowerCase &&
        isMinimum &&
        isNumeric &&
        isSpecial &&
        isUpperCase &&
        isPasswordsMatch
      ),
    [
      isLowerCase,
      isMinimum,
      isNumeric,
      isPasswordsMatch,
      isSpecial,
      isUpperCase,
    ]
  );

  const formData = useMemo(() => {
    const stepValues: {
      [key: number]: any;
    } = {
      1: {
        email,
        password,
        confirmedPassword,
      },
      2: {
        firstName: tasterData.firstName,
        lastName: tasterData.lastName,
        phoneNumber: tasterData.phoneNumber,
      },
      3: {
        milkAlternatives: tasterData.milkAlternatives,
        milkFrequency: tasterData.milkFrequency,
      },
      4: {
        tasteSense: tasterData.tasteSense,
        smellSense: tasterData.smellSense,
      },
    };

    const stepIsValid: {
      [key: number]: boolean;
    } = {
      1:
        Object.values(stepValues[1]).every((el) => el) &&
        validateEmail(email) &&
        isPasswordValid &&
        agreement,
      2: Object.values(stepValues[2]).every((el) => el),
      3:
        Object.values(stepValues[3]).every((el) => el) &&
        tasterData.milkAlternatives.length > 0,
      4: Object.values(stepValues[4]).every((el) => el),
      5: true,
      6: true,
    };

    return {
      values: stepValues[step],
      isValid: stepIsValid[step],
    };
  }, [
    agreement,
    confirmedPassword,
    email,
    isPasswordValid,
    password,
    step,
    tasterData.firstName,
    tasterData.lastName,
    tasterData.milkAlternatives,
    tasterData.milkFrequency,
    tasterData.phoneNumber,
    tasterData.smellSense,
    tasterData.tasteSense,
  ]);

  const handleFormClear = useCallback(() => {
    setEmail("");
    setPassword("");
    setConfirmedPassword("");
    setAgreement(false);
    setTasterData(initialTasterData);
  }, []);

  const handleStepChange = useCallback((step: number) => setStep(step), []);

  const onNextStep = useCallback(() => {
    //FIXME: Add the real request to the BE
    if (step === stepsCount) {
      setLoading(true);

      setTimeout(() => {
        setLoading(false);
        handleFormClear();
        console.log("Submit");
        handleRedirectToLogIn();
      }, 3000);
    } else {
      setStep((prev) => prev + 1);
    }
  }, [handleFormClear, handleRedirectToLogIn, step]);

  const handleSubmit = useCallback(() => {
    if (formData.isValid) {
      onNextStep();
    } else {
      checkFieldsValidation(formData.values);
    }
  }, [checkFieldsValidation, formData.isValid, formData.values, onNextStep]);

  const personalInfoStepContentData: StepContentData = useMemo(
    () => ({
      title: t("personal_info_title"),
      text: t("personal_info_text"),
      withImgUploader: true,
      fields: [
        {
          type: "input",
          placeholder: t("Enter Taster first name"),
          label: t("Taster first name"),
          required: true,
          value: tasterData.firstName,
          onChange: handleFieldChange("firstName"),
          disabled: loading,
          id: "taster-first-name",
          error: validFieldErrors.includes("firstName"),
          errorText: t("first_name_error"),
          onBlur: checkFieldValidation(tasterData.firstName, "firstName"),
        },
        {
          type: "input",
          placeholder: t("Enter Taster last name"),
          label: t("Taster last name"),
          required: true,
          value: tasterData.lastName,
          onChange: handleFieldChange("lastName"),
          disabled: loading,
          id: "taster-last-name",
          error: validFieldErrors.includes("lastName"),
          errorText: t("last_name_error"),
          onBlur: checkFieldValidation(tasterData.lastName, "lastName"),
        },
        {
          type: "phone",
          placeholder: t("Enter phone number"),
          label: t("Phone number"),
          required: true,
          value: tasterData.phoneNumber,
          onChange: handleFieldChange("phoneNumber"),
          disabled: loading,
          error: validFieldErrors.includes("phoneNumber"),
          errorText: t("phone_number_error"),
          onBlur: checkFieldValidation(tasterData.phoneNumber, "phoneNumber"),
        },
        {
          type: "date",
          label: t("Date of birth"),
          value: tasterData.dateOfBirth,
          onChange: handleFieldChange("dateOfBirth"),
          disabled: loading,
        },
        {
          type: "select",
          placeholder: t("Select Gender"),
          label: t("Gender"),
          value: tasterData.gender,
          options: genderSelectOptions,
          onChange: handleFieldChange("gender"),
          disabled: loading,
        },
        {
          type: "select",
          placeholder: t("Select Educational status"),
          label: t("Educational status"),
          value: tasterData.education,
          options: educationSelectOptions,
          onChange: handleFieldChange("education"),
          disabled: loading,
        },
        {
          type: "select",
          placeholder: t("Select Smoking status"),
          label: t("What is your smoking status?"),
          value: tasterData.smoking,
          options: smokingSelectOptions,
          onChange: handleFieldChange("smoking"),
          disabled: loading,
        },
      ],
    }),
    [
      t, // Add translation function to dependencies
      tasterData.firstName,
      tasterData.lastName,
      tasterData.phoneNumber,
      tasterData.dateOfBirth,
      tasterData.gender,
      tasterData.education,
      tasterData.smoking,
      handleFieldChange,
      loading,
      validFieldErrors,
      checkFieldValidation,
    ]
  );

  const dietaryPreferencesStepContentData: StepContentData = useMemo(
    () => ({
      title: t("Dietary Preferences and Habits"),
      text: t(
        "To provide us with more information about yourself, kindly complete the following fields. Rest assured that this data will be kept secure"
      ),
      fields: [
        {
          type: "multiselect",
          placeholder: t(
            "Select Which milk products and alternatives do you consume"
          ),
          label: t("Which milk products and alternatives do you consume?"),
          required: true,
          value: tasterData.milkAlternatives,
          onChange: handleFieldChange("milkAlternatives"),
          disabled: loading,
          options: milkAlternativesOptions,
          error: validFieldErrors.includes("milkAlternatives"),
          errorText: t(
            "Please select which milk products and alternatives do you consume"
          ),
        },
        {
          type: "select",
          placeholder: t("Select How frequently do you drink milk?"),
          label: t("How frequently do you drink milk?"),
          required: true,
          value: tasterData.milkFrequency,
          onChange: handleFieldChange("milkFrequency"),
          disabled: loading,
          options: setSelectOptions(milkFrequencyOptions),
          error: validFieldErrors.includes("milkFrequency"),
          errorText: t("Please enter a milk frequency"),
        },
        {
          type: "select",
          placeholder: t(
            "Select  how  frequently do you consume mayonnaise"
          ),
          label: t("How frequently do you consume mayonnaise?"),
          value: tasterData.mayonnaiseFrequency,
          onChange: handleFieldChange("mayonnaiseFrequency"),
          disabled: loading,
          options: setSelectOptions(mayonnaiseFrequencyOptions),
        },
        {
          type: "multiselect",
          placeholder: t("Select Which types of mayonnaise do you usually consume"),
          label: t("Which types of mayonnaise do you usually consume?"),
          value: tasterData.mayonnaiseTypes,
          onChange: handleFieldChange("mayonnaiseTypes"),
          disabled: loading,
          options: setSelectOptions(mayonnaiseTypesOptions),
        },
        {
          type: "multiselect",
          placeholder: t("Select specific flavor preferences"),
          label: t("Do you have any specific flavor preferences?"),
          value: tasterData.flavorPreferences,
          onChange: handleFieldChange("flavorPreferences"),
          disabled: loading,
          options: setSelectOptions(flavorPreferencesOptions),
        },
        {
          type: "multiselect",
          placeholder: t(
            "Select any substances you dislike or find unpleasant"
          ),
          label: t("Are there any substances you dislike or find unpleasant?"),
          value: tasterData.dislikedSubstances,
          onChange: handleFieldChange("dislikedSubstances"),
          disabled: loading,
          options: setSelectOptions(dislikedSubstancesOptions),
        },
        {
          type: "textarea",
          placeholder: t("Enter Other disliked substances"),
          label: t(
            "Any other substances you dislike which are not described above?"
          ),
          value: tasterData.otherDislikedSubstances,
          onChange: handleFieldChange("otherDislikedSubstances"),
          disabled: loading,
        },
        {
          type: "select",
          placeholder: t("Select Which type of diet do you follow"),
          label: t("Which type of diet do you follow?"),
          value: tasterData.dietType,
          options: setSelectOptions(dietTypeOptions),
          onChange: handleFieldChange("dietType"),
          disabled: loading,
        },
      ],
    }),
    [
      t, // Add translation function to dependencies
      tasterData.milkAlternatives,
      tasterData.milkFrequency,
      tasterData.mayonnaiseFrequency,
      tasterData.mayonnaiseTypes,
      tasterData.flavorPreferences,
      tasterData.dislikedSubstances,
      tasterData.otherDislikedSubstances,
      tasterData.dietType,
      handleFieldChange,
      loading,
      validFieldErrors,
      setSelectOptions,
    ]
  );

  const sensoryPerceptionStepContentData: StepContentData = useMemo(
    () => ({
      title: t("Sensory Perception"),
      text: t(
        "To provide us with more information about yourself, kindly complete the following fields. Rest assured that this data will be kept secure"
      ),
      fields: [
        {
          type: "select",
          placeholder: t("Select How would you rate your sense of taste"),
          label: t("How would you rate your sense of taste?"),
          required: true,
          value: tasterData.tasteSense,
          onChange: handleFieldChange("tasteSense"),
          disabled: loading,
          options: setSelectOptions(tasteSenseOptions),
          error: validFieldErrors.includes("tasteSense"),
          errorText: t("Please select how would you rate your sense of taste"),
        },
        {
          type: "select",
          placeholder: t("Select How would you rate your sense of smell"),
          label: t("How would you rate your sense of smell?"),
          required: true,
          value: tasterData.smellSense,
          onChange: handleFieldChange("smellSense"),
          disabled: loading,
          options: setSelectOptions(smellSenseOptions),
          error: validFieldErrors.includes("smellSense"),
          errorText: t("Please select how would you rate your sense of smell"),
        },
        {
          type: "select",
          placeholder: t("Select enjoy contrasting"),
          label: t(
            "Do you enjoy contrasting flavors in a dish, like sweet and salty?"
          ),
          value: tasterData.contrastingFlavorsEnjoying,
          onChange: handleFieldChange("contrastingFlavorsEnjoying"),
          disabled: loading,
          options: contrastingFlavorsEnjoyingOptions,
        },
        {
          type: "select",
          placeholder: t("Select specific herbs or spices"),
          label: t(
            "Can you detect subtle flavors in dishes, such as specific herbs or spices?"
          ),
          value: tasterData.subtleFlavorsDetecting,
          onChange: handleFieldChange("subtleFlavorsDetecting"),
          disabled: loading,
          options: subtleFlavorsDetectingOptions,
        },
      ],
    }),
    [
      t, // Add translation function to dependencies
      tasterData.tasteSense,
      tasterData.smellSense,
      tasterData.contrastingFlavorsEnjoying,
      tasterData.subtleFlavorsDetecting,
      handleFieldChange,
      loading,
      setSelectOptions,
      validFieldErrors,
    ]
  );

  const foodPreferencesStepContentData: StepContentData = useMemo(
    () => ({
      title: t("Cooking and Food Preferences"),
      text: t(
        "To provide us with more information about yourself, kindly complete the following fields. Rest assured that this data will be kept secure"
      ),
      fields: [
        {
          type: "select",
          placeholder: t("Select do you cook"),
          label: t("Do you cook?"),
          value: tasterData.cooking,
          onChange: handleFieldChange("cooking"),
          disabled: loading,
          options: cookingOptions,
        },
        {
          type: "multiselect",
          placeholder: t("Select what are you likely to eat"),
          label: t(
            "When you go out to dinner, what are you likely to eat?"
          ),
          value: tasterData.dinnerEating,
          onChange: handleFieldChange("dinnerEating"),
          disabled: loading,
          options: setSelectOptions(dinnerEatingOptions),
        },
        {
          type: "textarea",
          placeholder: t("Please enter what one dish can you eat every day"),
          label: t("What one dish can you eat every day?"),
          value: tasterData.everyDayDish,
          onChange: handleFieldChange("everyDayDish"),
          disabled: loading,
        },
        {
          type: "select",
          placeholder: t("Select do you eat spicy"),
          label: t("Do you eat spicy?"),
          value: tasterData.spicyEating,
          onChange: handleFieldChange("spicyEating"),
          disabled: loading,
          options: setSelectOptions(spicyEatingOptions),
        },
        {
          type: "multiselect",
          placeholder: t("Select What is your favorite texture in food?"),
          label: t("What is your favorite texture in food?"),
          value: tasterData.favoriteFoodTexture,
          onChange: handleFieldChange("favoriteFoodTexture"),
          disabled: loading,
          options: setSelectOptions(favoriteFoodTextureOptions),
        },
        {
          type: "select",
          placeholder: t("Select Which snacks do you prefer"),
          label: t("Which snacks do you prefer?"),
          value: tasterData.snacks,
          onChange: handleFieldChange("snacks"),
          disabled: loading,
          options: snacksOptions,
        },
        {
          type: "textarea",
          placeholder: t("Please enter your ultimate snack"),
          label: t("What is your ultimate snack?"),
          value: tasterData.ultimateSnack,
          onChange: handleFieldChange("ultimateSnack"),
          disabled: loading,
        },
        {
          type: "select",
          placeholder: t("Select if you add salt to most foods"),
          label: t("Do you add salt to most foods?"),
          value: tasterData.saltAdding,
          onChange: handleFieldChange("saltAdding"),
          disabled: loading,
          options: setSelectOptions(saltAddingOptions),
        },
      ],
    }),
    [
      t, // Add translation function to dependencies
      tasterData.cooking,
      tasterData.dinnerEating,
      tasterData.everyDayDish,
      tasterData.spicyEating,
      tasterData.favoriteFoodTexture,
      tasterData.snacks,
      tasterData.ultimateSnack,
      tasterData.saltAdding,
      handleFieldChange,
      loading,
      setSelectOptions,
    ]
  );

  const beveragePreferencesStepContentData: StepContentData = useMemo(
    () => ({
      title: t("Beverage Preferences & Changing Tastes and Experiences"),
      text: t(
        "To provide us with more information about yourself, kindly complete the following fields. Rest assured that this data will be kept secure"
      ),
      fields: [
        {
          type: "multiselect",
          placeholder: t("Select What drinks do you consume"),
          label: t("What drinks do you consume?"),
          value: tasterData.drinksConsuming,
          onChange: handleFieldChange("drinksConsuming"),
          disabled: loading,
          options: setSelectOptions(drinksConsumingOptions),
        },
        {
          type: "select",
          placeholder: t("Select Which of the following do you drink regularly"),
          label: t("Which of the following do you drink regularly?"),
          value: tasterData.regularDrink,
          onChange: handleFieldChange("regularDrink"),
          disabled: loading,
          options: setSelectOptions(regularDrinkOptions),
        },
        {
          type: "select",
          placeholder: t("Select How do you take your coffee in a cafe"),
          label: t("How do you take your coffee in a cafe?"),
          value: tasterData.coffeeInCafe,
          onChange: handleFieldChange("coffeeInCafe"),
          disabled: loading,
          options: setSelectOptions(coffeeInCafeOptions),
        },
        {
          type: "select",
          placeholder: t("Select How do you take your coffee at home"),
          label: t("How do you take your coffee at home?"),
          value: tasterData.coffeeAtHome,
          onChange: handleFieldChange("coffeeAtHome"),
          disabled: loading,
          options: setSelectOptions(coffeeAtHomeOptions),
        },
        {
          type: "select",
          placeholder: t("Select if you add sugar to your coffee"),
          label: t("Do you add sugar to your coffee?"),
          value: tasterData.sugarInCoffee,
          onChange: handleFieldChange("sugarInCoffee"),
          disabled: loading,
          options: setSelectOptions(sugarInCoffeeOptions),
        },
        {
          type: "select",
          placeholder: t("Select What milk do you use in your coffee"),
          label: t("What milk do you use in your coffee?"),
          value: tasterData.milkInCoffee,
          onChange: handleFieldChange("milkInCoffee"),
          disabled: loading,
          options: milkInCoffeeOptions,
        },
        {
          type: "select",
          placeholder: t(
            "Select What tea do you drink regularly (Not iced tea)"
          ),
          label: t("What tea do you drink regularly (Not iced tea)?"),
          value: tasterData.regularTea,
          onChange: handleFieldChange("regularTea"),
          disabled: loading,
          options: regularTeaOptions,
        },
        {
          type: "select",
          placeholder: t("Select Do you add sugar to your tea?"),
          label: t("Do you add sugar to your tea?"),
          value: tasterData.sugarInTea,
          onChange: handleFieldChange("sugarInTea"),
          disabled: loading,
          options: setSelectOptions(sugarInTeaOptions),
        },
      ],
    }),
    [
      t, // Add translation function to dependencies
      tasterData.drinksConsuming,
      tasterData.regularDrink,
      tasterData.coffeeInCafe,
      tasterData.coffeeAtHome,
      tasterData.sugarInCoffee,
      tasterData.milkInCoffee,
      tasterData.regularTea,
      tasterData.sugarInTea,
      handleFieldChange,
      loading,
      setSelectOptions,
    ]
  );

  const stepsContent: {
    [key: number]: JSX.Element;
  } = useMemo(
    () => ({
      1: (
        <InitialStep
          listOfPolicy={listOfPolicy}
          email={email}
          password={password}
          confirmedPassword={confirmedPassword}
          loading={loading}
          validFieldErrors={validFieldErrors}
          emailErrorText={emailErrorText}
          passwordErrorText={passwordErrorText}
          confirmedPasswordErrorText={confirmedPasswordErrorText}
          agreement={agreement}
          agreementErrorText={agreementErrorText}
          handleEmailChange={handleEmailChange}
          handlePasswordChange={handlePasswordChange}
          handleConfirmedPasswordChange={handleConfirmedPasswordChange}
          checkFieldValidation={checkFieldValidation}
          handleAgreementChange={handleAgreementChange}
        />
      ),
      2: <Step {...personalInfoStepContentData} />,
      3: <Step {...dietaryPreferencesStepContentData} />,
      4: <Step {...sensoryPerceptionStepContentData} />,
      5: <Step {...foodPreferencesStepContentData} />,
      6: <Step {...beveragePreferencesStepContentData} />,
    }),
    [
      agreement,
      agreementErrorText,
      beveragePreferencesStepContentData,
      checkFieldValidation,
      confirmedPassword,
      confirmedPasswordErrorText,
      dietaryPreferencesStepContentData,
      email,
      emailErrorText,
      foodPreferencesStepContentData,
      handleAgreementChange,
      handleConfirmedPasswordChange,
      handleEmailChange,
      handlePasswordChange,
      listOfPolicy,
      loading,
      password,
      passwordErrorText,
      personalInfoStepContentData,
      sensoryPerceptionStepContentData,
      validFieldErrors,
    ]
  );

  const currentStepContent = useMemo(
    () => stepsContent[step],
    [step, stepsContent]
  );

  return {
    loading,
    stepsCount,
    step,
    currentStepContent,
    validFieldErrors,
    handleSubmit,
    handleStepChange,
  };
};

export default useSignUp;
